// Calculation utilities for pricing and VAT

/**
 * Calculate line item totals based on pricing mode
 * @param {number} qty - Quantity
 * @param {number} price - Price per item
 * @param {number} discount - Discount value (0 = Retail, 10-40 = percentage, -1 = NETT)
 * @param {number} vatPerc - VAT percentage (default 23)
 * @returns {Object} { subtotal, vat, total }
 */
export const calculateLineItem = (qty, price, discount, vatPerc = 23) => {
  // Validate inputs
  if (qty === undefined || qty === null || price === undefined || price === null || discount === undefined || discount === null) {
    return { subtotal: 0, vat: 0, total: 0 };
  }

  // Parse to numbers
  const quantity = parseFloat(qty);
  const priceValue = parseFloat(price);
  const discountValue = parseFloat(discount);
  const vatPercValue = parseFloat(vatPerc);

  // Handle zero or invalid values
  if (isNaN(quantity) || isNaN(priceValue) || isNaN(discountValue) || isNaN(vatPercValue)) {
    return { subtotal: 0, vat: 0, total: 0 };
  }

  if (quantity === 0 || priceValue === 0) {
    return { subtotal: 0, vat: 0, total: 0 };
  }

  let subtotal = 0;
  let vat = 0;
  let total = 0;

  // NETT pricing (discount = -1)
  // Price is VAT-exclusive (cost price), just add VAT
  if (discountValue === -1) {
    subtotal = quantity * priceValue;
    vat = subtotal * (vatPercValue / 100);
    total = subtotal + vat;
  }
  // DISCOUNTED pricing (discount = 10-40%)
  // Price is VAT-exclusive, apply discount then add VAT
  else if (discountValue !== 0) {
    const value = quantity * priceValue;
    subtotal = value - (value * (discountValue / 100));
    vat = subtotal * (vatPercValue / 100);
    total = subtotal + vat;
  }
  // RETAIL pricing (discount = 0)
  // Price INCLUDES VAT, extract VAT portion
  else {
    total = quantity * priceValue;
    subtotal = total / ((vatPercValue / 100) + 1);
    vat = total - subtotal;
  }

  return {
    subtotal: parseFloat(subtotal.toFixed(2)),
    vat: parseFloat(vat.toFixed(2)),
    total: parseFloat(total.toFixed(2))
  };
};

/**
 * Calculate grand totals from array of line items
 * @param {Array} items - Array of line items with subtotal, vat, total
 * @returns {Object} { subtotal, vat, total }
 */
export const calculateGrandTotals = (items) => {
  if (!Array.isArray(items) || items.length === 0) {
    return { subtotal: 0, vat: 0, total: 0 };
  }

  return items.reduce((acc, item) => ({
    subtotal: acc.subtotal + (parseFloat(item.subtotal) || 0),
    vat: acc.vat + (parseFloat(item.vat) || 0),
    total: acc.total + (parseFloat(item.total) || 0)
  }), { subtotal: 0, vat: 0, total: 0 });
};

/**
 * Validate line items for form submission
 * @param {Array} items - Array of line items
 * @returns {Object} { ready: boolean, vat_ready: boolean }
 */
export const validateItems = (items) => {
  if (!Array.isArray(items) || items.length === 0) {
    return { ready: false, vat_ready: false };
  }

  // Check if all items have valid data
  const ready = items.every(item =>
    item.qty !== undefined &&
    item.qty !== null &&
    item.qty !== '' &&
    item.price !== undefined &&
    item.price !== null &&
    item.price !== '' &&
    item.discount !== undefined &&
    item.discount !== null &&
    item.discount !== '' &&
    (item.total || 0) > 0
  );

  // Check if all items have the same VAT percentage
  const firstVat = items[0]?.vat_perc;
  const vat_ready = firstVat !== undefined &&
    firstVat !== null &&
    items.every(item => item.vat_perc === firstVat);

  return { ready, vat_ready };
};

/**
 * Format number as currency (Euro)
 * @param {number} value - Number to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value) => {
  const number = parseFloat(value);
  if (isNaN(number)) return '€0.00';

  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(number);
};

/**
 * Format number to 2 decimal places
 * @param {number} value - Number to format
 * @returns {string} Formatted number
 */
export const formatDecimal = (value) => {
  const number = parseFloat(value);
  if (isNaN(number)) return '0.00';
  return number.toFixed(2);
};
